import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import EventSeriesManagement from './EventSeriesManagement';
import HostVolunteerRequests from './HostVolunteerRequests';
import AutomationSettings from './AutomationSettings';
import EventCategoryManagement from './EventCategoryManagement';
import PinballEventForm from '../components/PinballEventForm';
import EventsBottomNav from '../components/EventsBottomNav';
import EventsBrowse from '../components/EventsBrowse';
import { DSButton, DSCheckbox, DSIcon, DSInput, DSPageHeader, DSSelect, DSSheet, DSTabPanel, DSTabs, DSTextarea } from '../design-system/primitives';
import useEventsHub from '../hooks/useEventsHub';
import { isEventCancelled, isEventOver } from '../utils/eventHelpers';
import './EventsHub.css';

function EventsHub() {
  // The tab lives in the URL so a screen pushed from here can come back to where it
  // started. Returning to Events from a pushed screen used to land on Upcoming, which is
  // not where you were.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'browse');
  // Admin used to render its four sections stacked into one ~6000px scroll. They are
  // sections of one screen, so they are tabs.
  const [adminSection, setAdminSection] = useState(() => searchParams.get('section') || 'series');
  // Set when "Event series" is chosen from the create sheet; EventSeriesManagement opens
  // its own sheet on mount and hands the flag back.
  const [startSeriesCreate, setStartSeriesCreate] = useState(false);
  // Counts and status for the admin tabs, so a section says what is waiting in it before
  // you open it.
  const [adminCounts, setAdminCounts] = useState({
    activeSeries: 0,
    pendingRequests: 0,
    automationIdle: false
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPinballForm, setShowPinballForm] = useState(false);
  // One "Create" entry point that asks what kind, rather than a row of competing buttons
  const [showCreateChooser, setShowCreateChooser] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Which slice of the list the browse screen is showing: upcoming, mine or past. The
  // bottom nav's "My RSVPs" is a shortcut into the same screen with scope 'mine'.
  const [browseScope, setBrowseScope] = useState(() => (searchParams.get('scope') || 'upcoming'));

  const {
    events,
    myRsvps,
    loading,
    error,
    formData,
    setFormData,
    submitting,
    fetchEvents,
    handleSubmit: submitEvent,
    handleInputChange
  } = useEventsHub();

  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const handleSubmit = async (e) => {
    const created = await submitEvent(e);
    if (created) {
      setShowCreateForm(false);
    }
  };

  // The bottom nav and the segmented control are two ways into the same list, so both
  // set the scope and both keep the nav highlight pointing at where you actually are.
  const handleScopeChange = (nextScope) => {
    setBrowseScope(nextScope);
    setActiveTab(nextScope === 'mine' ? 'mine' : 'browse');
  };

  const handleSelectTab = (nextTab) => {
    setActiveTab(nextTab);

    if (nextTab === 'mine') {
      setBrowseScope('mine');
    } else if (nextTab === 'browse') {
      setBrowseScope((current) => (current === 'mine' ? 'upcoming' : current));
    }
  };

  const viewEventDetails = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (activeTab === 'browse' && browseScope === 'upcoming') {
      next.delete('tab');
      next.delete('scope');
      next.delete('section');
    } else {
      next.set('tab', activeTab);

      if (activeTab === 'admin') {
        next.set('section', adminSection);
        next.delete('scope');
      } else {
        next.set('scope', browseScope);
        next.delete('section');
      }
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // searchParams is deliberately excluded: it is the output of this effect, not an input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminSection, browseScope, setSearchParams]);

  useEffect(() => {
    if (!isAdmin || activeTab !== 'admin') {
      return;
    }

    let cancelled = false;

    const loadAdminCounts = async () => {
      // allSettled, not all: one failing call used to zero every count, so a rate-limited
      // automation status made the header read "0 active series".
      const [seriesRes, requestsRes, automationRes] = await Promise.allSettled([
        apiService.get('/event-series'),
        apiService.get('/host-volunteer-requests?status=pending'),
        apiService.get('/automation/status')
      ]);

      if (cancelled) return;

      const automation = automationRes.status === 'fulfilled'
        ? (automationRes.value.data?.eventAutomation || {})
        : null;

      setAdminCounts({
        activeSeries: seriesRes.status === 'fulfilled'
          ? (seriesRes.value.data || []).filter((item) => item.is_active).length
          : 0,
        pendingRequests: requestsRes.status === 'fulfilled'
          ? (requestsRes.value.data || []).length
          : 0,
        // Unknown is not idle: without a reading, do not raise an alarm
        automationIdle: automation
          ? !(automation.eventGeneration?.running && automation.hostReminders?.running)
          : false
      });
    };

    loadAdminCounts();

    return () => { cancelled = true; };
    // Deliberately not keyed on adminSection: /automation/status allows 20 requests per
    // 15 minutes, and refetching on every tab switch exhausted that in a minute of
    // clicking, which then blanked the Automation tab's own status card.
  }, [isAdmin, activeTab]);

  if (loading) return <div className="loading">Loading...</div>;

  // "1 active series - 7 events scheduled": what this screen is responsible for, at a
  // glance. Counts come from the admin summary endpoint so they match what the tabs show.
  const scheduledEventCount = events.filter((event) => !isEventOver(event) && !isEventCancelled(event)).length;
  const adminSummary = [
    `${adminCounts.activeSeries} active series`,
    `${scheduledEventCount} event${scheduledEventCount === 1 ? '' : 's'} scheduled`
  ].join(' \u00b7 ');

  return (
    <div className="events-hub-page bn-page-padding">
      {/* The browse screen carries its own title row with a live event count, so a second
          page header above it would just be the word "Events" twice. */}
      {activeTab === 'admin' && isAdmin && (
        <DSPageHeader
          title="Events"
          description={adminSummary}
          actions={(
            <DSButton
              appearance="solid"
              tone="primary"
              leadingIcon={<DSIcon name="plus" size="sm" />}
              onClick={() => setShowCreateChooser(true)}
            >
              Create
            </DSButton>
          )}
        />
      )}

      {error && <div className="error-message">{error}</div>}

      {/* One entry point, three destinations. Previously two buttons competed in the
          header and the third route (a series) was buried inside the Series section. */}
      {showCreateChooser && isAdmin && (
        <DSSheet title="What are you creating?" onClose={() => setShowCreateChooser(false)}>
          <div className="create-chooser">
            {[
              {
                key: 'event',
                label: 'Regular event',
                detail: 'A one-off night: board games, video games, open play.',
                onClick: () => {
                  setShowCreateChooser(false);
                  setShowPinballForm(false);
                  setShowCreateForm(true);
                }
              },
              {
                key: 'tournament',
                label: 'Pinball tournament',
                detail: 'A competition with a format, IFPA submission and results.',
                onClick: () => {
                  setShowCreateChooser(false);
                  setShowCreateForm(false);
                  setShowPinballForm(true);
                }
              },
              {
                key: 'series',
                label: 'Event series',
                detail: 'A recurring night, or a bounded league season.',
                onClick: () => {
                  setShowCreateChooser(false);
                  setAdminSection('series');
                  setStartSeriesCreate(true);
                }
              }
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className="create-chooser__option"
                onClick={option.onClick}
              >
                <span className="create-chooser__label">{option.label}</span>
                <span className="create-chooser__detail">{option.detail}</span>
              </button>
            ))}
          </div>
        </DSSheet>
      )}

      {/* Pinball Tournament Form Modal */}
      {showPinballForm && isAdmin && (
        <div className="modal-overlay">
          <div className="modal-content">
            <PinballEventForm
              onClose={() => setShowPinballForm(false)}
              onSuccess={() => {
                setShowPinballForm(false);
                fetchEvents();
              }}
            />
          </div>
        </div>
      )}

      {/* Upcoming, My RSVPs and Past are three scopes of one list, so they are one
          screen with a segmented control rather than three tab bodies that repeated
          each other's card markup. */}
      {activeTab !== 'admin' && (
        <EventsBrowse
          events={events}
          myRsvps={myRsvps}
          currentUser={currentUser}
          scope={browseScope}
          onScopeChange={handleScopeChange}
          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}
          onOpenEvent={viewEventDetails}
        />
      )}

      {/* Admin Tab (Admin Only): event creation, series, and categories */}
      {activeTab === 'admin' && isAdmin && (
        <div className="tab-content">
          {showCreateForm && (
            <div className="create-event-form">
              <h2>Create New Event</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <DSInput
                    label="Event Title *"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Weekly Pinball Night"
                  />
                </div>

                <div className="form-group">
                  <DSTextarea
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Tell people what to expect..."
                  />
                </div>

                <div className="form-group">
                  <DSSelect
                    label="Event Category *"
                    name="event_category"
                    value={formData.event_category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="pinball">Pinball</option>
                    <option value="video_game">Video Game</option>
                    <option value="board_game">Board Game</option>
                    <option value="book_club">Book Club</option>
                    <option value="other">Other</option>
                  </DSSelect>
                </div>

                <div className="form-group">
                  <DSInput
                    label="Date & Time *"
                    type="datetime-local"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <DSInput
                    label="End time"
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                  />
                  <small>Leave empty for four hours. An end before the start time runs past midnight.</small>
                </div>

                <div className="form-group">
                  <DSCheckbox
                    id="create-event-private"
                    name="is_private"
                    label="Private event"
                    checked={formData.is_private}
                    onChange={handleInputChange}
                  />
                  <small>
                    Kept off the website and the events lists. People with the link can still open it
                    and RSVP. The hallway sign shows only the hosts and the time.
                  </small>
                </div>

                {formData.is_private && (
                  <div className="form-group">
                    <DSInput
                      label="Hallway sign label"
                      type="text"
                      placeholder="Leave empty to show only the time"
                      value={formData.event_metadata?.hallway_label || ''}
                      onChange={(e) => {
                        const { hallway_label: _previous, ...rest } = formData.event_metadata || {};
                        const label = e.target.value;
                        setFormData({
                          ...formData,
                          event_metadata: label ? { ...rest, hallway_label: label } : rest
                        });
                      }}
                    />
                    <small>Shown under the hosts on the hallway sign instead of the event title.</small>
                  </div>
                )}

                <div className="form-actions">
                  <DSButton
                    type="button"
                    appearance="outline"
                    tone="neutral"
                    onClick={() => setShowCreateForm(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </DSButton>
                  <DSButton
                    type="submit"
                    appearance="solid"
                    tone="primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : 'Create Event'}
                  </DSButton>
                </div>
              </form>
            </div>
          )}

          <DSTabs
            ariaLabel="Admin sections"
            className="admin-tabs"
            activeKey={adminSection}
            onSelect={setAdminSection}
            items={[
              { key: 'series', label: 'Series', count: adminCounts.activeSeries },
              { key: 'categories', label: 'Categories' },
              { key: 'requests', label: 'Requests', count: adminCounts.pendingRequests },
              { key: 'automation', label: 'Automation', dot: adminCounts.automationIdle ? 'warning' : undefined }
            ]}
          />

          {/* The one thing that silently stops everything working, said once, at the top,
              with a way straight to it. */}
          {adminCounts.automationIdle && adminSection !== 'automation' && (
            <button
              type="button"
              className="admin-idle-banner"
              onClick={() => setAdminSection('automation')}
            >
              <DSIcon name="warning" size="sm" tone="warning" />
              <span className="admin-idle-banner__main">
                <span className="admin-idle-banner__title">Automation is idle</span>
                <span className="admin-idle-banner__detail">
                  Event generation and host reminders are off, so nothing is being created or sent.
                </span>
              </span>
              <DSIcon name="chevronRight" size="sm" tone="warning" />
            </button>
          )}

          <DSTabPanel tabKey="series" activeKey={adminSection}>
            <EventSeriesManagement
              embedded
              autoOpenCreate={startSeriesCreate}
              onAutoOpenCreateHandled={() => setStartSeriesCreate(false)}
            />
          </DSTabPanel>

          <DSTabPanel tabKey="categories" activeKey={adminSection}>
            <EventCategoryManagement embedded />
          </DSTabPanel>

          <DSTabPanel tabKey="requests" activeKey={adminSection}>
            <HostVolunteerRequests embedded />
          </DSTabPanel>

          <DSTabPanel tabKey="automation" activeKey={adminSection}>
            <AutomationSettings embedded />
          </DSTabPanel>
        </div>
      )}

      <EventsBottomNav activeTab={activeTab} onSelectTab={handleSelectTab} isAdmin={isAdmin} />
    </div>
  );
}

export default EventsHub;
